import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardPaste, Check, X, AlertCircle, Trash2 } from "lucide-react";

// Field aliases — maps common spreadsheet header variants to our internal field keys.
// Lowercased + alphanumerics-only on both sides during matching.
const HEADER_ALIASES = {
  name: ["name", "fullname", "full", "contactname", "studentname", "leadname", "firstname"],
  email: ["email", "emailaddress", "mail", "e", "emailid"],
  phone: ["phone", "mobile", "tel", "telephone", "contact", "contactnumber", "phonenumber", "mob", "number"],
  source: ["source", "leadsource", "channel", "how", "via", "referral"],
  session_preference: ["session", "sessionpreference", "preference", "day", "class", "preferredsession", "time"],
  notes: ["notes", "comments", "comment", "remarks", "details", "info"],
  enquiry_date: ["date", "enquirydate", "enquired", "dateenquired", "enquireddate"],
  status: ["status", "leadstatus", "stage"],
  starter_pack: ["starterpack", "starter", "pack"],
  first_payment: ["firstpayment", "deposit"],
  // Students-only:
  term: ["term", "intake", "cohort"],
  session_key: ["sessionkey", "session", "day", "class"],
  amount_paid: ["amountpaid", "paid", "payment"],
  payment_status: ["paymentstatus", "paid?", "paymentstate"],
  student_type: ["studenttype", "type", "returner"],
};

const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");

// Detect delimiter — prefer tab (Excel default), else comma, else any whitespace gap.
function splitRow(line) {
  if (line.includes("\t")) return line.split("\t").map(c => c.trim());
  // CSV (handle quoted commas)
  if (line.includes(",")) {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { q = !q; continue; }
      if (ch === "," && !q) { out.push(cur.trim()); cur = ""; continue; }
      cur += ch;
    }
    out.push(cur.trim());
    return out;
  }
  // Whitespace
  return line.split(/\s{2,}/).map(c => c.trim());
}

function parsePaste(text, fields) {
  const lines = text.replace(/\r/g, "").split("\n").map(l => l.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], mapping: {}, hasHeader: false };
  const firstCells = splitRow(lines[0]);
  // Decide whether the first row is a header: at least 60% of cells match a known alias.
  let hasHeader = false;
  let mapping = {};
  let matches = 0;
  for (let i = 0; i < firstCells.length; i++) {
    const n = norm(firstCells[i]);
    for (const f of fields) {
      const aliases = (HEADER_ALIASES[f] || []).map(norm);
      if (aliases.includes(n)) { mapping[i] = f; matches++; break; }
    }
  }
  if (matches >= Math.max(2, Math.ceil(firstCells.length * 0.5))) hasHeader = true;
  // If no clear header, guess column types by content of the first 3 data rows.
  if (!hasHeader) {
    mapping = {};
    const sample = lines.slice(0, Math.min(3, lines.length)).map(splitRow);
    const colCount = sample[0]?.length || 0;
    for (let c = 0; c < colCount; c++) {
      let isEmail = 0; let isPhone = 0; let isDate = 0;
      for (const s of sample) {
        const v = (s[c] || "").trim();
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) isEmail++;
        else if (/^[+()\-\s\d]{7,}$/.test(v) && (v.match(/\d/g) || []).length >= 7) isPhone++;
        else if (/^\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/.test(v)) isDate++;
      }
      if (isEmail >= 2) mapping[c] = "email";
      else if (isPhone >= 2) mapping[c] = "phone";
      else if (isDate >= 2) mapping[c] = "enquiry_date";
    }
    // First unassigned column becomes Name (most common convention)
    if (!Object.values(mapping).includes("name")) {
      for (let c = 0; c < colCount; c++) {
        if (!mapping[c]) { mapping[c] = "name"; break; }
      }
    }
  }
  // Build rows
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const rows = dataLines.map((line, i) => {
    const cells = splitRow(line);
    const r = { _k: `row-${i}-${Math.random().toString(36).slice(2, 8)}` };
    cells.forEach((cell, i) => {
      const f = mapping[i];
      if (f && cell) r[f] = cell;
    });
    return r;
  }).filter((r) => r.name);
  return { rows, mapping, hasHeader, headerCells: hasHeader ? firstCells : null, columnCount: firstCells.length };
}

const FIELD_LABELS_LEAD = {
  "": "(skip)",
  name: "Name *",
  email: "Email",
  phone: "Phone",
  source: "Source",
  session_preference: "Session pref.",
  notes: "Notes",
  enquiry_date: "Enquiry date",
  status: "Status",
  starter_pack: "Starter pack",
  first_payment: "First payment",
};
const FIELD_LABELS_STUDENT = {
  "": "(skip)",
  name: "Name *",
  email: "Email",
  phone: "Phone",
  term: "Term",
  session_key: "Session",
  student_type: "Type (new/returner)",
  amount_paid: "Amount paid",
  payment_status: "Payment status",
  notes: "Notes",
};

const SESSION_OPTS = [
  { v: "tue_am", lbl: "Tuesday AM (10–1)" },
  { v: "tue_pm", lbl: "Tuesday PM (2–5)" },
  { v: "fri_eve", lbl: "Friday Evening (6–8:30)" },
];

export default function BulkPasteModal({ kind, onClose, onSubmit, settings }) {
  const isStudents = kind === "students";
  const FIELD_LABELS = isStudents ? FIELD_LABELS_STUDENT : FIELD_LABELS_LEAD;
  const fieldKeys = Object.keys(FIELD_LABELS);

  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [defaultTerm, setDefaultTerm] = useState(settings?.current_term || "Summer 2026");
  const [defaultSession, setDefaultSession] = useState("tue_am");
  const [busy, setBusy] = useState(false);

  const termOptions = useMemo(() => {
    const arr = (settings?.terms || []).map((t) => t.term).filter(Boolean);
    if (settings?.current_term && !arr.includes(settings.current_term)) arr.unshift(settings.current_term);
    return arr.length ? arr : [defaultTerm];
  }, [settings, defaultTerm]);

  // Re-parse whenever the textarea changes (debounce-free; cheap operation).
  useEffect(() => {
    if (!text.trim()) { setParsed(null); return; }
    setParsed(parsePaste(text, fieldKeys.filter(Boolean)));
  }, [text, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMapping = (colIndex, newField) => {
    if (!parsed) return;
    const newMap = { ...parsed.mapping };
    // Strip the new field from any other column that had it (each field used once).
    Object.keys(newMap).forEach((k) => { if (newMap[k] === newField) delete newMap[k]; });
    if (newField) newMap[colIndex] = newField; else delete newMap[colIndex];
    // Rebuild rows from the original textarea using the new mapping.
    const lines = text.replace(/\r/g, "").split("\n").map(l => l.trim()).filter(Boolean);
    const dataLines = parsed.hasHeader ? lines.slice(1) : lines;
    const rows = dataLines.map((line, i) => {
      const cells = splitRow(line);
      const r = { _k: `row-${i}-${Math.random().toString(36).slice(2, 8)}` };
      cells.forEach((cell, i) => {
        const f = newMap[i];
        if (f && cell) r[f] = cell;
      });
      return r;
    }).filter((r) => r.name);
    setParsed({ ...parsed, mapping: newMap, rows });
  };

  const removeRow = (idx) => {
    if (!parsed) return;
    setParsed({ ...parsed, rows: parsed.rows.filter((_, i) => i !== idx) });
  };

  const editRowField = (idx, field, value) => {
    if (!parsed) return;
    const rows = [...parsed.rows];
    rows[idx] = { ...rows[idx], [field]: value };
    setParsed({ ...parsed, rows });
  };

  const submit = async () => {
    if (!parsed?.rows?.length) { toast.error("Nothing to import"); return; }
    if (!parsed.rows.every((r) => r.name)) {
      toast.error("Every row needs a Name. Please map a Name column or remove empty rows.");
      return;
    }
    setBusy(true);
    try {
      const cleanRows = parsed.rows.map(({ _k, ...rest }) => rest);
      const body = isStudents
        ? { rows: cleanRows, default_term: defaultTerm, default_session: defaultSession }
        : { rows: cleanRows };
      const res = await onSubmit(body);
      const { imported, updated, skipped, errors } = res || {};
      toast.success(`Imported ${imported || 0} · Updated ${updated || 0} · Skipped ${skipped || 0}`);
      if (errors?.length) {
        console.warn("Bulk paste errors:", errors);
        toast.warning(`${errors.length} row${errors.length === 1 ? "" : "s"} had issues — see console.`);
      }
      onClose();
    } catch (e) {
      // Surface a useful message instead of the generic "Import failed". We try
      // (in priority order): the server-supplied detail, the HTTP status, and
      // finally the JS error name. Also dump the full error to the console so
      // power users can paste it back in a bug report.
      console.error("[bulk-paste] import failed", e);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const code = e?.code; // axios codes like 'ERR_NETWORK'
      let msg;
      if (detail) {
        msg = `Import failed: ${typeof detail === "string" ? detail : JSON.stringify(detail).slice(0, 160)}`;
      } else if (status) {
        msg = `Import failed (HTTP ${status}). Try refreshing the page and signing in again.`;
      } else if (code === "ERR_NETWORK" || /Network Error/i.test(e?.message || "")) {
        msg = "Import failed: lost connection. Check your internet and try again.";
      } else {
        msg = `Import failed: ${e?.message || "unknown error"}`;
      }
      toast.error(msg, { duration: 8000 });
    }
    finally { setBusy(false); }
  };

  const tipText = isStudents
    ? "Copy student rows from your spreadsheet (with or without headers) and paste below. We'll auto-detect Name, Email, Phone, etc. Set the default Term and Session — these apply to any rows where those columns are missing."
    : "Copy lead rows from your spreadsheet (with or without headers) and paste below. We'll auto-detect Name, Email, Phone, Source, etc. We match existing leads by Name + Phone — duplicates get merged with any new info you've added.";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="admin-surface w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col my-6" onClick={(e) => e.stopPropagation()} data-testid={`bulk-paste-${kind}-modal`}>
        <div className="flex items-start justify-between p-5 border-b border-[#E5E0D8]">
          <div>
            <h3 className="font-display text-2xl text-[#5D2C8F] flex items-center gap-2"><ClipboardPaste size={20} /> Paste from spreadsheet · {isStudents ? "Students" : "Leads"}</h3>
            <p className="text-xs text-[#6E6074] mt-1 max-w-3xl">{tipText}</p>
          </div>
          <button onClick={onClose} className="text-[#A685BD] p-1" data-testid="bulk-paste-close"><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {isStudents && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-[#FAF6EE] rounded-lg">
              <div>
                <label className="text-sm text-[#5D2C8F]">Default term (for rows without one)</label>
                <select value={defaultTerm} onChange={(e) => setDefaultTerm(e.target.value)}
                  className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2"
                  data-testid="bulk-paste-default-term">
                  {termOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#5D2C8F]">Default session (for rows without one)</label>
                <select value={defaultSession} onChange={(e) => setDefaultSession(e.target.value)}
                  className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2"
                  data-testid="bulk-paste-default-session">
                  {SESSION_OPTS.map((o) => <option key={o.v} value={o.v}>{o.lbl}</option>)}
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="text-sm text-[#5D2C8F]">Paste rows from Excel / Google Sheets</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 font-mono text-xs"
              placeholder={isStudents
                ? "e.g.\nName\tEmail\tPhone\tTerm\tSession\nJoanne Smith\tjoanne@example.com\t07700 900123\tSummer 2026\ttue_am"
                : "e.g.\nName\tEmail\tPhone\tSource\tNotes\nJoanne Smith\tjoanne@example.com\t07700 900123\tInstagram\tInterested in Tuesday AM"}
              data-testid="bulk-paste-textarea"
            />
            <div className="text-xs text-[#6E6074] mt-1">Tip: select rows in your spreadsheet → ⌘C / Ctrl+C → click in the box above → ⌘V / Ctrl+V.</div>
          </div>

          {parsed && (
            <>
              <div className="flex items-center justify-between">
                <div className="text-sm text-[#5D2C8F]">
                  <strong>{parsed.rows.length}</strong> row{parsed.rows.length === 1 ? "" : "s"} parsed
                  {parsed.hasHeader && <span className="text-[#6E6074] text-xs"> · header row detected</span>}
                </div>
                {parsed.rows.length === 0 && (
                  <div className="text-xs text-[#A685BD] flex items-center gap-1"><AlertCircle size={12} /> No usable rows. Make sure each row has a Name value.</div>
                )}
              </div>

              {/* Column mapping */}
              <div className="overflow-x-auto border border-[#E5E0D8] rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAF6EE]">
                    <tr>
                      {Array.from({ length: parsed.columnCount || 0 }).map((_, ci) => (
                        <th key={`col-${ci}`} className="px-2 py-2 border-r border-[#E5E0D8] last:border-r-0 min-w-[140px]">
                          <select
                            value={parsed.mapping[ci] || ""}
                            onChange={(e) => updateMapping(ci, e.target.value)}
                            className="w-full bg-white border border-[#E5E0D8] rounded px-1.5 py-1 text-[11px] text-[#5D2C8F] font-semibold"
                            data-testid={`bulk-paste-col-map-${ci}`}
                          >
                            {fieldKeys.map((k) => <option key={k} value={k}>{FIELD_LABELS[k]}</option>)}
                          </select>
                          {parsed.headerCells && (
                            <div className="text-[10px] text-[#6E6074] mt-1 truncate" title={parsed.headerCells[ci]}>{parsed.headerCells[ci] || "(no header)"}</div>
                          )}
                        </th>
                      ))}
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 50).map((row, idx) => (
                      <tr key={row._k || `idx-${idx}`} className="border-t border-[#E5E0D8]" data-testid={`bulk-paste-row-${idx}`}>
                        {Array.from({ length: parsed.columnCount || 0 }).map((_, ci) => {
                          const f = parsed.mapping[ci];
                          const v = f ? (row[f] || "") : "";
                          return (
                            <td key={`cell-${ci}`} className="px-2 py-1.5 border-r border-[#E5E0D8] last:border-r-0 align-top">
                              {f
                                ? <input value={v} onChange={(e) => editRowField(idx, f, e.target.value)} className="w-full bg-transparent text-[#3A2A47] text-[11px] border-0 px-0 py-0 focus:outline-none focus:bg-white focus:border focus:rounded focus:px-1" />
                                : <span className="text-[#9a8aa6] text-[10px] italic">(skipped)</span>}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5 text-right">
                          <button onClick={() => removeRow(idx)} className="text-[#A685BD]" aria-label="Remove row" data-testid={`bulk-paste-remove-row-${idx}`}><Trash2 size={11} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.rows.length > 50 && (
                  <div className="px-3 py-2 text-xs text-[#6E6074] bg-[#FAF6EE] border-t border-[#E5E0D8]">
                    Showing first 50 of {parsed.rows.length} rows. All will be imported on submit.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-[#E5E0D8] bg-[#FAF6EE]">
          <button onClick={onClose} className="text-sm text-[#6E6074]" data-testid="bulk-paste-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!parsed?.rows?.length || busy}
            className="sac-btn-primary disabled:opacity-50"
            data-testid="bulk-paste-submit"
          >
            <Check size={14} /> {busy ? "Importing…" : `Import ${parsed?.rows?.length || 0} row${(parsed?.rows?.length || 0) === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
